import React, { useEffect, useState } from "react";
import { Modal, Input, Form, message, Alert } from "antd";
import { modalTitle } from "../../../components";
import { useCart } from "../../../Context";
import { useSaveQuotationMutation } from "../../../Services";
import dayjs from "dayjs";

interface QuotationSaveModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

export const QuotationSaveModal: React.FC<QuotationSaveModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const {
    cart,
    customer,
    editingQuotationId,
    editingQuotationName,
    quotationTempCustomer,
    setEditingQuotationId,
    setEditingQuotationName,
    setQuotationTempCustomer,
    clearCart,
  } = useCart();

  const [form] = Form.useForm();
  const saveMutation = useSaveQuotationMutation();

  useEffect(() => {
    if (open) {
      // Set initial values
      let initialName = editingQuotationName || "";
      if (!initialName) {
        const dateStr = dayjs().format("YYYY-MM-DD");
        if (customer) {
          initialName = `${customer.name} - Quote (${dateStr})`;
        } else if (quotationTempCustomer?.name) {
          initialName = `${quotationTempCustomer.name} - Quote (${dateStr})`;
        } else {
          initialName = `Walk-in Quote (${dateStr})`;
        }
      }

      form.setFieldsValue({
        quotation_name: initialName,
        temp_customer_name: quotationTempCustomer?.name || "",
        temp_customer_phone: quotationTempCustomer?.phone || "",
        temp_customer_email: quotationTempCustomer?.email || "",
        temp_customer_address: quotationTempCustomer?.address || "",
      });
    }
  }, [open, customer, editingQuotationName, quotationTempCustomer, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (cart.length === 0) {
        message.warning("Cart is empty. Nothing to save.");
        return;
      }

      const payload = {
        id: editingQuotationId || null,
        customer_id: customer?.id ? String(customer.id) : null,
        quotation_name: values.quotation_name.trim(),
        cart_data: cart.map((i) => ({
          ...i,
          printed_qty: i.qty,
        })),
        temp_customer_name: customer
          ? null
          : values.temp_customer_name?.trim() || null,
        temp_customer_phone: customer
          ? null
          : values.temp_customer_phone?.trim() || null,
        temp_customer_email: customer
          ? null
          : values.temp_customer_email?.trim() || null,
        temp_customer_address: customer
          ? null
          : values.temp_customer_address?.trim() || null,
      };

      saveMutation.mutate(payload, {
        onSuccess: (res) => {
          if (res?.success) {
            message.success(
              editingQuotationId
                ? "Quotation updated successfully."
                : "Quotation saved successfully.",
            );
            clearCart();
            if (onSuccess) {
              onSuccess();
            }
            onCancel();
          } else {
            message.error(res?.message || "Failed to save quotation.");
          }
        },
        onError: (err: any) => {
          message.error(
            err?.message || "An error occurred while saving quotation.",
          );
        },
      });
    } catch (err) {
      // Form validation errors handled automatically by Ant Design Form
    }
  };

  return (
    <Modal
      title={modalTitle(
        editingQuotationId ? "Update Quotation" : "Save as Quotation",
      )}
      open={open}
      onCancel={onCancel}
      onOk={handleSave}
      okText={editingQuotationId ? "Update" : "Save Quotation"}
      confirmLoading={saveMutation.isPending}
      width={480}
      okButtonProps={{
        size: "middle",
        className: "bg-indigo-600 hover:bg-indigo-700",
      }}
      cancelButtonProps={{ size: "middle" }}
    >
      <div className="py-2">
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            name="quotation_name"
            label={
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Quotation Name / Label
              </span>
            }
            rules={[
              { required: true, message: "Please enter quotation name." },
            ]}
          >
            <Input placeholder="e.g. Project Proposal Quote" size="middle" />
          </Form.Item>

          {customer ? (
            <div className="mb-4">
              <Alert
                message="Assigned Customer"
                description={
                  <div className="text-xs">
                    <span className="font-semibold">{customer.name}</span>
                    {customer.credit_limit !== undefined && (
                      <span className="block text-gray-400 mt-0.5">
                        Credit Limit: LKR{" "}
                        {customer.credit_limit.toLocaleString()}
                      </span>
                    )}
                  </div>
                }
                type="info"
                showIcon
              />
            </div>
          ) : (
            <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
              <span className="block text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">
                Temporary Customer Details
              </span>
              <Form.Item
                name="temp_customer_name"
                label={
                  <span className="text-[10px] font-semibold text-gray-500 uppercase">
                    Customer Name
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: "Please enter temporary customer name.",
                  },
                ]}
              >
                <Input placeholder="John Doe" size="middle" />
              </Form.Item>

              <div className="grid grid-cols-2 gap-3">
                <Form.Item
                  name="temp_customer_phone"
                  label={
                    <span className="text-[10px] font-semibold text-gray-500 uppercase">
                      Phone No
                    </span>
                  }
                >
                  <Input placeholder="+94 77 123 4567" size="middle" />
                </Form.Item>

                <Form.Item
                  name="temp_customer_email"
                  label={
                    <span className="text-[10px] font-semibold text-gray-500 uppercase">
                      Email
                    </span>
                  }
                >
                  <Input placeholder="customer@email.com" size="middle" />
                </Form.Item>
              </div>

              <Form.Item
                name="temp_customer_address"
                label={
                  <span className="text-[10px] font-semibold text-gray-500 uppercase">
                    Billing Address
                  </span>
                }
                className="mb-0"
              >
                <Input.TextArea
                  placeholder="Enter customer address..."
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  size="middle"
                />
              </Form.Item>
            </div>
          )}
        </Form>
      </div>
    </Modal>
  );
};
